import { z } from 'zod';

/**
 * Backend `promo_library` section of `GET tpo_gen/config/` — the single source of
 * truth for what the Promo Library grid shows.
 *
 * The backend owns every semantic decision: which attributes and KPIs exist,
 * their order, labels, numeric formatting, editability, which attributes are
 * filterable and under which dimension, plus page size, default sort and whether
 * the change toggle is offered. `fe_config.promoLibraryConfig` contributes only
 * presentation (UI copy, filter-panel section labels, overflow actions).
 *
 * Strictness follows the rest of the config gate: render-critical leaves (`key`,
 * `display_name`, `data_type`) are required so a malformed entry fails loud at
 * the gate rather than rendering a blank column, while everything the FE can
 * default is tolerant. `.passthrough()` keeps fields the backend adds ahead of
 * the FE (`data_table`, `row_id_column`, …).
 */

/** Numeric/scalar shape of a KPI — drives cell formatting and the edit control. */
export const promoLibraryDataTypeSchema = z.enum(['decimal', 'integer', 'boolean', 'text', 'select']);

export const promoLibraryAttributeSchema = z
    .object({
        key: z.string().min(1),
        display_name: z.string().min(1),
        /** Filter dimension the attribute belongs to (`dim1`, `dim2`, `mechanic`, …). */
        dimension: z.string().min(1).optional(),
        /** Offered in the global filter panel. Non-filterable attributes are display-only. */
        filterable: z.boolean().optional().default(false),
        default_visible: z.boolean().optional().default(true),
        order: z.number().optional().default(0),
    })
    .passthrough();

export const promoLibraryKpiOptionSchema = z
    .object({
        key: z.string(),
        display_name: z.string(),
    })
    .passthrough();

export const promoLibraryKpiSchema = z
    .object({
        key: z.string().min(1),
        display_name: z.string().min(1),
        data_type: promoLibraryDataTypeSchema,
        /** Fraction digits. Absent for `boolean` / `text` / `select`. */
        precision: z.number().optional(),
        /** Rendered before the value (e.g. `"$"`); also the edit input's addon. */
        prefix: z.string().nullable().optional(),
        /** Rendered after the value (e.g. `"%"`). */
        suffix: z.string().nullable().optional(),
        /** When true the cell is clickable and opens the edit modal. */
        editable: z.boolean().optional().default(false),
        /** Selected in the KPI picker on first load. */
        default_visible: z.boolean().optional().default(false),
        order: z.number().optional().default(0),
        min_value: z.number().nullable().optional(),
        max_value: z.number().nullable().optional(),
        /** Fixed option set; present only for `data_type: 'select'` KPIs. */
        options: z.array(promoLibraryKpiOptionSchema).nullable().optional(),
        /** Default selection for `data_type: 'select'` KPIs when no value is stored. */
        default_value: z.union([z.string(), z.number()]).nullable().optional(),
        /** Legacy flat option set; `options` takes precedence for `select` KPIs. */
        allowed_values: z.array(z.union([z.string(), z.number()])).nullable().optional(),
    })
    .passthrough();

export const promoLibraryGroupSchema = z
    .object({
        key: z.string().min(1),
        display_name: z.string().min(1).optional(),
        attributes: z.array(promoLibraryAttributeSchema).optional().default([]),
        kpis: z.array(promoLibraryKpiSchema).optional().default([]),
        page_size: z.number().optional().default(100),
        default_sort_key: z.string().min(1).optional(),
        show_change_toggle: z.boolean().optional().default(false),
        /** Leading attribute columns pinned while the grid scrolls horizontally. */
        pinned_attribute_count: z.number().optional().default(2),
    })
    .passthrough();

export const promoLibrarySchema = z
    .object({
        // Presence of the section is the intent signal, so an omitted flag reads
        // as enabled; only an explicit `false` switches the feature off.
        enabled: z.boolean().optional().default(true),
        library_groups: z.array(promoLibraryGroupSchema).optional().default([]),
    })
    .passthrough();

export type PromoLibraryBackendKpiOption = z.infer<typeof promoLibraryKpiOptionSchema>;
export type PromoLibraryBackendAttribute = z.infer<typeof promoLibraryAttributeSchema>;
export type PromoLibraryBackendKpi = z.infer<typeof promoLibraryKpiSchema>;
export type PromoLibraryBackendGroup = z.infer<typeof promoLibraryGroupSchema>;
export type PromoLibraryBackendConfig = z.infer<typeof promoLibrarySchema>;
